import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useExpedientes } from '../lib/hooks';
import { Card, Pill, Skeleton, cn } from './ui';
import { Clock, FileText, CalendarClock, ShieldCheck } from 'lucide-react';

interface Reminder {
  id: string;
  cliente: string;
  doc: string;
  category: string;
  expiry: string | null;
  vencido: boolean;
}

type FilterKey = 'todos' | 'por_vencer' | 'vencido';

/** Full-width, horizontally-scrollable rail of upcoming document expirations. */
export function RemindersWidget() {
  const navigate = useNavigate();
  const { data: exps, isLoading } = useExpedientes();
  const [filter, setFilter] = useState<FilterKey>('todos');

  const allItems = useMemo<Reminder[]>(
    () =>
      (exps ?? []).flatMap((e) =>
        e.docs
          .filter((d) => d.estado === 'Por Vencer' || d.estado === 'Vencido')
          .map((d) => ({
            id: e.id,
            cliente: e.name,
            doc: d.name,
            category: d.category,
            expiry: d.expiry,
            vencido: d.estado === 'Vencido',
          }))
      ),
    [exps]
  );

  const hasBoth = allItems.some((r) => r.vencido) && allItems.some((r) => !r.vencido);

  const items = useMemo<Reminder[]>(() => {
    if (filter === 'por_vencer') return allItems.filter((r) => !r.vencido);
    if (filter === 'vencido') return allItems.filter((r) => r.vencido);
    return allItems;
  }, [allItems, filter]);

  return (
    <section data-tour="home-recordatorios">
      <div className="mb-4 flex items-center gap-2.5">
        <span className="grid h-8 w-8 place-items-center rounded-lg bg-tone-amber-bg text-tone-amber">
          <Clock className="h-4 w-4" strokeWidth={1.75} />
        </span>
        <h2 className="font-serif text-2xl font-medium text-ink">Recordatorios</h2>
        {allItems.length > 0 && (
          <span className="rounded-full bg-tone-amber-bg px-2 py-0.5 font-mono text-[10px] font-bold text-tone-amber">
            {allItems.length}
          </span>
        )}
      </div>

      {/* filter tabs — only shown when both states are present */}
      {!isLoading && hasBoth && (
        <div className="mb-3 flex gap-1.5">
          {([['todos', 'Todos'], ['por_vencer', 'Por vencer'], ['vencido', 'Vencido']] as const).map(([key, label]) => (
            <button
              key={key}
              onClick={() => setFilter(key)}
              className={cn(
                'rounded-full px-3 py-1 text-xs font-bold transition-colors',
                filter === key
                  ? 'bg-primary text-white'
                  : 'bg-canvas text-ink-soft hover:bg-line hover:text-ink'
              )}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {isLoading ? (
        <div className="-mx-4 flex snap-x snap-mandatory gap-4 overflow-x-auto px-4 pb-2 sm:-mx-6 sm:px-6 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-[148px] w-64 shrink-0 rounded-3xl" />
          ))}
          <span className="w-4 shrink-0 sm:w-6" aria-hidden="true" />
        </div>
      ) : items.length === 0 ? (
        <Card className="flex items-center gap-3 text-ink-soft">
          <ShieldCheck className="h-5 w-5 text-primary-dark" strokeWidth={1.75} />
          <span className="text-sm font-semibold">Sin vencimientos próximos. Todo al día.</span>
        </Card>
      ) : (
        <div className="-mx-4 flex snap-x snap-mandatory gap-4 overflow-x-auto px-4 pb-2 sm:-mx-6 sm:px-6 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {items.map((r, i) => (
            <button
              key={i}
              onClick={() => navigate(`/expedientes/${r.id}`)}
              className="flex w-64 shrink-0 snap-start flex-col gap-3 rounded-3xl border border-line bg-panel p-4 text-left shadow-card transition-all hover:-translate-y-0.5 hover:shadow-pop"
            >
              <div className="flex items-center justify-between">
                <span
                  className={
                    'grid h-11 w-11 place-items-center rounded-2xl ' +
                    (r.vencido ? 'bg-tone-red-bg text-tone-red' : 'bg-tone-amber-bg text-tone-amber')
                  }
                >
                  {r.vencido ? <CalendarClock className="h-5 w-5" strokeWidth={1.75} /> : <FileText className="h-5 w-5" strokeWidth={1.75} />}
                </span>
                <Pill tone={r.vencido ? 'red' : 'amber'}>{r.vencido ? 'Vencido' : 'Por vencer'}</Pill>
              </div>
              <div className="min-w-0">
                <p className="line-clamp-2 text-sm font-bold leading-snug text-ink">{r.doc}</p>
                <p className="mt-0.5 truncate text-xs text-ink-soft">{r.cliente}</p>
              </div>
              <p className="font-mono text-[10.5px] text-ink-faint">
                {r.category}
                {r.expiry ? ` · vence ${r.expiry}` : ''}
              </p>
            </button>
          ))}
          <span className="w-4 shrink-0 sm:w-6" aria-hidden="true" />
        </div>
      )}
    </section>
  );
}
